import {
  AnyPermission,
  CreateUpdateAny,
  IAny,
  PaginatedResult,
  RlsGroup,
  RlsUser,
  UpdateRlsGroup,
  UpdateRlsUser,
} from "@/types/breeze/Any";
import { BaseService } from "../BaseService";
import { formatAnyName, singularize } from "@/utils/text";
import { EntityFieldTree } from "@/types/breeze/Entity";

export class AnyService extends BaseService {
  public async getAll<T = IAny>(
    name: string,
    fields: string[] = [],
    filter: string = "",
    page: number = 1,
    pageSize: number = 100,
    sortBy: string = "",
    sortDirection: string = "",
  ): Promise<PaginatedResult<T>> {
    const sortParams = sortBy
      ? `&sortBy=${sortBy}&sortDirection=${sortDirection || "asc"}`
      : "";
    return this.getRequest(
      `/entities/${name}/items?page=${page}&pageSize=${pageSize}${
        fields.length > 0 ? `&fields=${fields.join(",")}` : ""
      }${filter ? `&${filter}` : ""}${sortParams}`,
    );
  }

  public async getById<T = IAny>(
    name: string,
    id: number,
    fields: string[] = [],
  ): Promise<T> {
    return this.getRequest(
      `/entities/${name}/items/${id}${
        fields.length > 0 ? `?fields=${fields.join(",")}` : ""
      }`,
    );
  }

  public async getRlsUsers(name: string, id: number): Promise<RlsUser[]> {
    return this.getRequest(`/entities/${name}/items/${id}/rls-users`);
  }

  public async getRlsGroups(name: string, id: number): Promise<RlsGroup[]> {
    return this.getRequest(`/entities/${name}/items/${id}/rls-groups`);
  }

  public async getEntityLevelPermissions(name: string): Promise<AnyPermission> {
    return this.getRequest(`/entities/${name}/items/permissions`);
  }

  public async getRecordLevelPermissions(
    name: string,
    id: number,
  ): Promise<AnyPermission> {
    return this.getRequest(`/entities/${name}/items/${id}/permissions`);
  }

  public async createAny(name: string, entity: CreateUpdateAny): Promise<IAny> {
    return this.postRequest(
      `/entities/${name}/items`,
      entity,
      `${singularize(formatAnyName(name))} created`,
    );
  }

  public async createMany(name: string, entities: IAny[]): Promise<IAny> {
    return this.postRequest(
      `/entities/${name}/items/bulk`,
      entities,
      `${singularize(formatAnyName(name))} created`,
    );
  }

  public async import(name: string, entities: IAny[]): Promise<IAny> {
    return this.postRequest(
      `/entities/${name}/items/import`,
      entities,
      `Data records for ${formatAnyName(name)} imported successfully`,
    );
  }

  public async hierarchy(name: string): Promise<EntityFieldTree[]> {
    return this.getRequest(`/entities/${name}/hierarchy`);
  }

  public async updateAny(
    name: string,
    id: number,
    entity: CreateUpdateAny,
    suppressSuccessMessage: boolean = false,
  ): Promise<IAny> {
    return this.putRequest(
      `/entities/${name}/items/${id}`,
      entity,
      `${singularize(formatAnyName(name))} updated`,
      true,
      suppressSuccessMessage,
    );
  }

  public async updateRlsUser(
    name: string,
    id: number,
    user: UpdateRlsUser,
  ): Promise<void> {
    return this.putRequest(
      `/entities/${name}/items/${id}/rls-users`,
      user,
      "RLS user updated",
    );
  }

  public async updateRlsGroup(
    name: string,
    id: number,
    group: UpdateRlsGroup,
  ): Promise<void> {
    return this.putRequest(
      `/entities/${name}/items/${id}/rls-groups`,
      group,
      "RLS group updated",
    );
  }

  public async updateLockedStatus(
    name: string,
    id: number,
    locked: boolean,
  ): Promise<void> {
    return this.postRequest(
      `/entities/${name}/items/${id}/set-lock-status?locked=${locked}`,
      null,
      "Record locked status updated",
    );
  }

  public async deleteAny(name: string, id: number): Promise<void> {
    return this.deleteRequest(`/entities/${name}/items/${id}`, "Item deleted");
  }

  public async deleteManyAny(name: string, ids: number[]): Promise<void> {
    return this.postRequest(
      `/entities/${name}/items/delete-many`,
      ids,
      `${ids.length} items deleted`,
    );
  }

  public async deleteRlsUser(
    name: string,
    id: number,
    rlsUserId: number,
  ): Promise<void> {
    return this.deleteRequest(
      `/entities/${name}/items/${id}/rls-users/${rlsUserId}`,
      "RLS user deleted",
    );
  }

  public async deleteRlsGroup(
    name: string,
    id: number,
    rlsGroupId: number,
  ): Promise<void> {
    return this.deleteRequest(
      `/entities/${name}/items/${id}/rls-groups/${rlsGroupId}`,
      "RLS group deleted",
    );
  }
}

export const anyService = new AnyService();
